import React, { Component } from 'react'
import { scaleLinear } from 'd3-scale'
import { interpolateRgb } from 'd3-interpolate'
import moment from 'moment'
import { Scales, Margins, SvgDimensions, Data, Tick } from './index'
import { DataPoint, UsageData } from '../../store'
// eslint-disable-next-line
// @ts-ignore : This folder is copied at docker runtime
import { themeDetails } from '../../theme/theme'

// import themeColors from '../../styles/base/_theme.scss'
import themeColors from '../../styles/base/_themevariables.module.scss'

import ViewChart from '../../utils/ViewChart'
import Axis from './Axis'
moment.locale('fi')

type Props = {
  data: Data
  margins: Margins
  maxValue: number
  scales: Scales
  svgDimensions: SvgDimensions
  tick?: Tick
  unit: string
  showComparison: boolean
  showTemperature?: boolean
  usageData?: UsageData
  updateUsageData: (value: React.SetStateAction<any>) => void // eslint-disable-line
  isProductionData: boolean
  maximumResolution: string
  loading: boolean
  onBarHover?: (ts: string, x: number) => void
  onBarLeave?: () => void
}

type frameType = {
  electricity: boolean
  heat: boolean
  water: boolean
}

class Bars extends Component<Props> {
  drill = (ts: string): void => {
    const { usageData, updateUsageData } = this.props
    const newStartDate = moment(ts)
    let newEndDate

    if (usageData && usageData.frame === 'year' && usageData.level === 'month') {
      newEndDate = moment(newStartDate).endOf('month')
      updateUsageData({
        ...usageData,
        start: newStartDate.format('YYYY-MM-DD'),
        end: newEndDate.format('YYYY-MM-DD'),
        frame: 'month',
        level: 'day',
      })
    } else if (usageData && usageData.frame === 'month' && usageData.level === 'day') {
      newEndDate = moment(newStartDate).endOf('day')
      updateUsageData({
        ...usageData,
        start: newStartDate.format('YYYY-MM-DD'),
        end: newEndDate.format('YYYY-MM-DD'),
        frame: 'day',
        level: 'hour',
      })
    } else if (usageData && usageData.frame === 'week' && usageData.level === 'day') {
      newEndDate = moment(newStartDate).endOf('day')
      updateUsageData({
        ...usageData,
        start: newStartDate.format('YYYY-MM-DD'),
        end: newEndDate.format('YYYY-MM-DD'),
        frame: 'day',
        level: 'hour',
      })
    } else if (usageData && usageData.frame === 'custom' && usageData.level === 'day') {
      newEndDate = moment(newStartDate).endOf('day')

      updateUsageData({
        ...usageData,
        start: newStartDate.format('YYYY-MM-DD'),
        end: newEndDate.format('YYYY-MM-DD'),
        frame: 'day',
        level: 'hour',
      })
    } else if (
      usageData &&
      (usageData.frame === 'day' || usageData.frame === 'week') &&
      usageData.level === 'hour'
    ) {
      newEndDate = moment(ts).endOf('hour')
      updateUsageData({
        ...usageData,
        start: newStartDate.format('YYYY-MM-DD HH:mm:ss'),
        end: newEndDate.format('YYYY-MM-DD HH:mm:ss'),
        frame: 'hour',
        level: 'quarter-hour',
      })
    }
  }

  isDrillable = (usageData?: UsageData, maximumResolution?: string): boolean | frameType => {
    if (!usageData) return false
    const { frame } = usageData

    if (frame === 'hour') return false
    if (frame === 'day' && maximumResolution === 'PT15M') return ViewChart.day
    if (frame === 'day') return false
    if (frame === 'year') return ViewChart.month

    return ViewChart.day
  }

  notFutureDate = (ts: string): boolean => {
    if (moment(ts).isSameOrAfter(moment(), 'day')) {
      return false
    }
    return true
  }

  render(): JSX.Element {
    const {
      scales,
      margins,
      data,
      svgDimensions,
      tick,
      maxValue,
      showComparison,
      usageData,
      isProductionData,
      maximumResolution,
      loading,
      onBarHover,
      onBarLeave,
    } = this.props

    const { xScale, yScale } = scales
    const { height } = svgDimensions

    let type = ''
    if (usageData && usageData.type && themeDetails.services.indexOf(usageData.type) > -1) {
      type = usageData.type
    } else {
      type = 'electricity'
    }

    let xOrient = ''
    let translateXAxis = ''

    if (isProductionData) {
      xOrient = 'Top'
      translateXAxis = 'translate(0, 10)'
    } else {
      xOrient = 'Bottom'
      translateXAxis = `translate(0, ${height - margins.bottom})`
    }
    /**
     * Color scale values for different bar - line chart cases
     */
    const colorScale = scaleLinear<string>()
      .domain([0, maxValue])
      .range([themeColors[`${type}-bar-primary-light`], themeColors[`${type}-bar-primary-dark`]])
      .interpolate(interpolateRgb)

    const colorScale2 = scaleLinear<string>()
      .domain([0, maxValue])
      .range([themeColors[`${type}-bar-secondary-light`], themeColors[`${type}-bar-secondary-dark`]])
      .interpolate(interpolateRgb)

    const colorScale3 = scaleLinear<string>()
      .domain([0, maxValue])
      .range([themeColors[`${type}-bar-tertiary-light`], themeColors[`${type}-bar-tertiary-dark`]])
      .interpolate(interpolateRgb)

    const colorScale4 = scaleLinear<string>()
      .domain([0, maxValue])
      .range([themeColors[`${type}-bar-quaternary-light`], themeColors[`${type}-bar-quaternary-dark`]])
      .interpolate(interpolateRgb)

    const colorScale5 = scaleLinear<string>()
      .domain([0, maxValue])
      .range([themeColors[`${type}-bar-cBN02-light`], themeColors[`${type}-bar-cBN02-dark`]])
      .interpolate(interpolateRgb)

    const colorScale6 = scaleLinear<string>()
      .domain([0, maxValue])
      .range([themeColors[`${type}-bar-pBN02-light`], themeColors[`${type}-bar-pBN02-dark`]])
      .interpolate(interpolateRgb)

    const yScale2 = scaleLinear()
      .domain([maxValue, 0])
      .range([margins.top, svgDimensions.height - margins.bottom])

    const lineHeight = 40

    const vpY = () => yScale2(0)
    const vpHeight = (d: DataPoint) =>
      yScale2(0) - yScale2(Math.round((d.vp = d.vp || 0) * 1000) / 1000)

    const vpFill = (d: DataPoint) =>
      `${
        d.pBN === 'BN02'
          ? colorScale6((d.vp = d.vp || 0))
          : colorScale3((d.vp = d.vp || 0))
      }`

    const vpBarlineY = (d: DataPoint) =>
      yScale2(0) - yScale2(Math.round((d.vp = d.vp || 0) * 1000) / 1000)

    const vpComparisonFill = (d: DataPoint) => `${colorScale4((d.vp = d.vp || 0))}`

    const vY = (d: DataPoint) => yScale(Math.round((d.v = d.v || 0) * 1000) / 1000)

    const vFill = (d: DataPoint) =>
      `${d.cBN === 'BN02' ? colorScale5((d.v = d.v || 0)) : colorScale((d.v = d.v || 0))}`

    const vHeight = (d: DataPoint) => height - margins.bottom - yScale((d.v = d.v || 0))
    const vBarlineY = (d: DataPoint) =>
      yScale(Math.round((d.v = d.v || 0) * 1000) / 1000) - lineHeight
    const vComparisonFill = (d: DataPoint) => `${colorScale2((d.v = d.v || 0))}`

    let bars
    if (data.length > 0) {
      const drillable = this.isDrillable(usageData, maximumResolution)
      const widthMod = showComparison ? 0.5 : 1.0
      const barWidth = xScale.bandwidth() * widthMod

      const className = `bar${drillable ? ' drillable' : ''} ${loading ? ' loading' : ''}`

      bars = data
        .slice(0)
        .reverse()
        .map((d: DataPoint, i) => {
          const comparison = d?.comparison
          const ts = d.ts
          const currentBarCenterX = Number(xScale(ts)) + barWidth / 2
          const comparisonBarCenterX = Number(xScale(ts)) + barWidth + barWidth / 2

          return (
            <svg className="bar-wrapper" key={i}>
              <>
                <rect
                  className={className}
                  x={`${xScale(ts)}`}
                  y={isProductionData ? vpY : vY(d)}
                  height={isProductionData ? vpHeight(d) : vHeight(d)}
                  onClick={() => drillable && this.notFutureDate(ts) && this.drill(ts)}
                  onMouseEnter={() => onBarHover?.(ts, currentBarCenterX)}
                  onMouseLeave={() => onBarLeave?.()}
                  width={`${barWidth}`}
                  fill={isProductionData ? vpFill(d) : vFill(d)}
                  transform={isProductionData ? 'translate(0, 10)' : ''}
                />
                <rect
                  className="bar-line"
                  x={`${xScale(ts) + barWidth / 2}`}
                  y={isProductionData ? vpBarlineY(d) : vBarlineY(d)}
                  height={lineHeight}
                  width="1"
                  transform={isProductionData ? 'translate(0, 10)' : ''}
                />
              </>

              {showComparison && comparison && (
                <>
                  <rect
                    className={`${className} comparison-bar`}
                    x={xScale(ts) + barWidth}
                    y={isProductionData ? vpY : vY(comparison)}
                    height={isProductionData ? vpHeight(comparison) : vHeight(comparison)}
                    width={barWidth}
                    fill={isProductionData ? vpComparisonFill(comparison) : vComparisonFill(comparison)}
                    transform={isProductionData ? 'translate(0, 10)' : ''}
                    onClick={() => drillable && this.notFutureDate(ts) && this.drill(ts)}
                    onMouseEnter={() => onBarHover?.(ts, comparisonBarCenterX)}
                    onMouseLeave={() => onBarLeave?.()}
                  />
                  <rect
                    className="bar-line"
                    x={`${xScale(ts) + barWidth * 1.5}`}
                    y={isProductionData ? vpBarlineY(comparison) : vBarlineY(comparison)}
                    height={lineHeight}
                    width="1"
                  />
                </>
              )}
            </svg>
          )
        })
    }

    return (
      <g className="graph-bars">
        {bars}
        <Axis
          data={data}
          orient={xOrient}
          scale={scales.xScale}
          tick={tick}
          tickSize={height - margins.top - margins.bottom}
          translate={translateXAxis}
          type={isProductionData ? 'production' : 'consumption'}
        />
      </g>
    )
  }
}

export default Bars